/**
 * Base API configuration and utilities
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Export API_BASE_URL
export { API_BASE_URL };

/**
 * API client with common HTTP methods
 */
export const apiClient = {
  /**
   * GET request
   */
  async get(endpoint, params = {}) {
    try {
      const queryString = new URLSearchParams(
        Object.entries(params).filter(([_, value]) => value !== '' && value !== null)
      ).toString();
      const url = `${API_BASE_URL}${endpoint}${queryString ? '?' + queryString : ''}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`API GET Error (${endpoint}):`, error);
      throw error;
    }
  },

  /**
   * POST request with JSON body
   */
  async post(endpoint, data, options = {}) {
    try {
      const fetchOpts = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      };
      if (options.credentials) {
        fetchOpts.credentials = options.credentials;
      }
      const response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOpts);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`API POST Error (${endpoint}):`, error);
      throw error;
    }
  },

  /**
   * POST request with FormData (for file uploads)
   */
  async postFormData(endpoint, formData) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`API POST FormData Error (${endpoint}):`, error);
      throw error;
    }
  },

  /**
   * PUT request
   */
  async put(endpoint, data) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`API PUT Error (${endpoint}):`, error);
      throw error;
    }
  },

  /**
   * DELETE request
   */
  async delete(endpoint) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`API DELETE Error (${endpoint}):`, error);
      throw error;
    }
  },
};

/**
 * Base URL for Django-uploaded media (/media/...).
 * Prefer REACT_APP_MEDIA_BASE_URL; otherwise derive from REACT_APP_API_URL so dev (CRA on :3000, API on :8000) loads images correctly.
 */
function getMediaBaseUrl() {
  const explicit = process.env.REACT_APP_MEDIA_BASE_URL;
  if (explicit) {
    return explicit.replace(/\/$/, '');
  }
  const apiUrl = process.env.REACT_APP_API_URL || '';
  if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
    return apiUrl.replace(/\/api\/?$/, '').replace(/\/$/, '');
  }
  return '';
}

/**
 * Helper to build full URL for media files (photos)
 */
export const getMediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('blob:')) return path;
  const baseUrl = getMediaBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

export default apiClient;
